import * as React from 'react';
import { assertNever } from '~/Partials/Utils/typeUtils';

const CheckIfWidthMatchesOptions = (width: number, options: WindowWidthDetectionOptions) => {
    switch (options.condition) {
        case 'greaterThan':
            return width > options.breakpoint;
        case 'greaterOrEqual':
            return width >= options.breakpoint;
        case 'lessThan':
            return width < options.breakpoint;
        case 'lessOrEqual':
            return width <= options.breakpoint;
        case 'inRange':
            return width >= options.lowerBreakpoint && width <= options.upperBreakpoint;
        case 'inRangeExclusive':
            return width > options.lowerBreakpoint && width < options.upperBreakpoint;
        default:
            assertNever(options);
    }
};

interface WindowWidthDetectionOptionsSingleArgument {
    condition: 'greaterThan' | 'lessThan' | 'greaterOrEqual' | 'lessOrEqual';
    breakpoint: number;
}

interface WindowWidthDetectionOptionsTwoArguments {
    condition: 'inRange' | 'inRangeExclusive';
    lowerBreakpoint: number;
    upperBreakpoint: number;
}

export type WindowWidthDetectionOptions =
    | WindowWidthDetectionOptionsSingleArgument
    | WindowWidthDetectionOptionsTwoArguments;

export const useWindowWidthDetection = (options: WindowWidthDetectionOptions) => {
    const [matchesOptions, setMatchesOptions] = React.useState<boolean>();

    const getDependencies = () => {
        switch (options.condition) {
            case 'greaterThan':
            case 'greaterOrEqual':
            case 'lessThan':
            case 'lessOrEqual':
                return [options.condition, options.breakpoint];
            case 'inRange':
            case 'inRangeExclusive':
                return [options.condition, options.lowerBreakpoint, options.upperBreakpoint];

            default:
                assertNever(options);
        }
    };

    React.useEffect(() => {
        const getWindowSize = () => {
            const { innerWidth: width, innerHeight: height } = window;

            return {
                width,
                height,
            };
        };

        const handleResize = () => {
            const size = getWindowSize();
            const matches = CheckIfWidthMatchesOptions(size.width, options);

            setMatchesOptions(matches);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, getDependencies());

    return matchesOptions;
};
